// Super Admin — the blank order-form library.
//
// Order forms are shared per schema GROUP and live in the files bucket at
// `New/templates/orders_templates/{group}/{name}.pdf`. A clinic whose
// `schema_settings."Group"` is unset reads a folder named after its own schema,
// so ungrouped clinics get a private set rather than inheriting someone else's.
//
// Doctors never come here — they pick from their group's list when generating
// an order. This is where the list itself is maintained.
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Text, Select, Spinner, useToast, Flex,
  Divider,
} from "@chakra-ui/react";
import {
  listAllOrderTemplates, uploadOrderTemplate, deleteOrderTemplate,
} from "../../services/orderPaperwork";

const MAX_MB = 10;

export default function OrderTemplatesModal({ isOpen, onClose }) {
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const toast = useToast();

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAllOrderTemplates();
      const list = res?.groups || [];
      setGroups(list);
      setGroup((g) => g || (list[0] ? list[0].group : ""));
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) reload();
  }, [isOpen, reload]);

  const current = groups.find((g) => g.group === group);

  const onPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!group) {
      toast({ title: "Choose a group first", status: "warning", duration: 2500 });
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({ title: `That PDF is over ${MAX_MB} MB`, status: "error", duration: 3000 });
      return;
    }
    setBusy(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await uploadOrderTemplate({
        group, filename: file.name, fileBase64: base64,
      });
      toast({ title: `Added ${res.label}`, status: "success", duration: 2500 });
      await reload();
    } catch (err) {
      toast({ title: "Upload failed", description: err?.message, status: "error", duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (name) => {
    setBusy(true);
    try {
      await deleteOrderTemplate({ group, name });
      toast({ title: "Template removed", status: "info", duration: 2000 });
      await reload();
    } catch (err) {
      toast({ title: "Could not remove it", description: err?.message, status: "error", duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Order Form Templates</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.500" mb={3}>
            Blank order forms, shared by every clinic in a group. Doctors pick
            from this list when they generate an order, and the AI fills the
            form with the items they selected.
          </Text>

          {loading ? (
            <Flex align="center" gap={3} py={8} justify="center">
              <Spinner size="sm" /> <Text fontSize="sm">Loading templates…</Text>
            </Flex>
          ) : error ? (
            <Box borderWidth="1px" borderColor="red.300" bg="red.50" p={3} borderRadius="md">
              <Text fontSize="sm" fontWeight="semibold" color="red.700">{error}</Text>
            </Box>
          ) : (
            <>
              <Text fontSize="xs" fontWeight="semibold" mb={1}>Group</Text>
              <Select
                size="sm"
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                mb={1}
              >
                {groups.map((g) => (
                  <option key={g.group} value={g.group}>
                    {g.group} ({g.templates.length})
                  </option>
                ))}
              </Select>
              {current && (
                <Text fontSize="xs" color="gray.500" fontFamily="mono" mb={3}>
                  {current.folder}
                </Text>
              )}

              <Divider my={3} />

              {!current || current.templates.length === 0 ? (
                <Text fontSize="sm" color="gray.500" py={4} textAlign="center">
                  No forms in this group yet.
                </Text>
              ) : (
                <Box>
                  {current.templates.map((t) => (
                    <Flex
                      key={t.name}
                      align="center"
                      gap={2}
                      borderWidth="1px"
                      borderRadius="md"
                      px={3}
                      py={2}
                      mb={2}
                    >
                      <Box minW={0}>
                        <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                          {t.label}
                        </Text>
                        <Text fontSize="xs" color="gray.500" fontFamily="mono" noOfLines={1}>
                          {t.name}
                        </Text>
                      </Box>
                      <Button
                        size="xs"
                        ml="auto"
                        colorScheme="red"
                        variant="outline"
                        isDisabled={busy}
                        onClick={() => onRemove(t.name)}
                      >
                        Remove
                      </Button>
                    </Flex>
                  ))}
                </Box>
              )}

              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                style={{ display: "none" }}
                onChange={onPick}
              />
              <Button
                mt={2}
                size="sm"
                colorScheme="teal"
                isLoading={busy}
                isDisabled={!group}
                onClick={() => fileRef.current && fileRef.current.click()}
              >
                + Upload a PDF form
              </Button>
              <Text fontSize="xs" color="gray.500" mt={2}>
                PDF only, up to {MAX_MB} MB. The filename becomes the form's
                name, so call it something a doctor will recognise.
              </Text>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
